// components/BranchSelector/BranchSelector.jsx
import { CaretDownOutlined } from "@ant-design/icons";
import { Select } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { switchBranch } from "@api/adminUsers";
import { error, warning, question } from "@alert";
import { handleLogout } from "@features/auth/authActions";
import { switchCurrentBranch } from "@store/slice/current-slice";
import { setPermissions, setToken, setFeatures } from "@store/slice/user-slice";
import {
  logPermissionsDebug,
  ITEM_PRICES_PERMISSION_TRACE,
  tracePermissionSubmoduleStage,
} from "@helpers/logPermissionsDebug";
import { convertKeysToCamelCase } from "@utils/utils";
import Preloader from "@components/Preloader/Preloader";
import UserProfileMenu from "@components/UserProfile/UserProfileMenu";
import { confirmPageLeaveIfDirty } from "@helpers/pageLeaveGuard";
import { sortBranchesPutTestLast } from "@helpers/sortBranchesPutTestLast";

/** 這些頁面換分店時由列表自身骨架載入（與請採驗列表一致），不蓋全螢幕 Preloader */
const SALES_SKELETON_ON_BRANCH_SWITCH = new Set([
  "/sales/checkoutrecord",
  "/sales/consumablesrecord",
  "/sales/balance-payment",
]);

const LOGOUT_LOGO_URL = `${process.env.PUBLIC_URL}/assets/img/header/loginout.svg`;

const BranchLogoCircle = ({ imageUrl, onError }) => {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [imageUrl]);

  const showSkeleton = !loaded;

  return (
    <span className='branch-select-logo'>
      {showSkeleton ? (
        <span className='branch-select-logo__skeleton' aria-hidden='true' />
      ) : null}
      <img
        ref={imgRef}
        src={imageUrl}
        alt=''
        className={showSkeleton ? "branch-select-logo__img--loading" : undefined}
        onLoad={() => setLoaded(true)}
        onError={() => {
          setLoaded(true);
          onError?.();
        }}
      />
    </span>
  );
};

/** 視覺微調 class，定義於 _new-header.scss（.branch-select-slot-nudge--*） */
const BranchSelector = ({ showUserProfile = true, wrapperClassName = "" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useSelector((state) => state.user.user);
  const token = userInfo?.token;
  const currentBranchId = useSelector((state) => state.current.branchId);

  const [isLoading, setIsLoading] = useState(false);
  const [imageErrors, setImageErrors] = useState({});

  const branchList = useMemo(
    () => sortBranchesPutTestLast(userInfo?.brandInfo?.branches || []),
    [userInfo?.brandInfo?.branches]
  );
  const brandLogo = userInfo.brandInfo?.brand_logo_url;
  const brandName = userInfo.brandInfo?.brand_name;

  const handleImageError = (branchId) => {
    setImageErrors((prev) => ({ ...prev, [branchId]: true }));
  };

  const renderBranchLabel = useCallback(
    (branch, { isLogout = false } = {}) => {
      const imageUrl = isLogout
        ? LOGOUT_LOGO_URL
        : branch.branch_logo_url || brandLogo;
      const hasError = !isLogout && imageErrors[branch.id];
      const shouldShowEmpty = !imageUrl || hasError;

      return (
        <div className='d-flex align-items-center'>
          {shouldShowEmpty ? (
            <span className='branch-select-logo branch-select-logo--empty' />
          ) : (
            <BranchLogoCircle
              imageUrl={imageUrl}
              onError={isLogout ? undefined : () => handleImageError(branch.id)}
            />
          )}
          <span className='ms-2'>{branch.branch_name}</span>
        </div>
      );
    },
    [brandLogo, imageErrors]
  );

  const confirmLogout = useCallback(() => {
    question(2, 
      "確定要登出嗎？", 
      "", 
      "確定", 
      "取消", 
      () => {
      handleLogout(dispatch);
      navigate("/");
    });
  }, [dispatch, navigate]);

  const handleSwitchBranch = (id) => {
    if (String(id) === String(currentBranchId)) return;

    confirmPageLeaveIfDirty(() => {
      performSwitchBranch(id);
    });
  };

  const performSwitchBranch = async (id) => {
    try {
      setIsLoading(true);

      const res = await switchBranch(token, { switchBranchId: id });
      if (res.data.status === "success") {
        dispatch(setToken({ token: res.data.data }));
        const branchInfo = convertKeysToCamelCase(
          branchList.find((b) => b.id === id)
        );
        dispatch(switchCurrentBranch({ branchId: id, branchInfo }));
        // logPermissionsDebug(
        //   res.data.permissions,
        //   "切換分店（BranchSelector）→ Redux"
        // );
        // tracePermissionSubmoduleStage(
        //   "切換分店 → Redux（目前帳號有效權限）",
        //   ITEM_PRICES_PERMISSION_TRACE,
        //   { nestedPermissions: res.data.permissions }
        // );
        dispatch(setPermissions({ permissions: res.data.permissions }));
        dispatch(
          setFeatures({
            features: res.data.features,
          })
        );

        localStorage.setItem("permissions", JSON.stringify(res.data.permissions));
        localStorage.setItem("revxToken", res.data.data);
        if (res.data.features) {
          localStorage.setItem("features", JSON.stringify(res.data.features));
        }
      } else {
        warning(1, 
          "尚未獲得分店權限",
          "", 
          "我了解了"
        );
      }
    } catch (err) {
      console.log("分店切換失敗", err);
      error(1, 
        "分店切換失敗", 
        err, 
        "確認"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const branchesOption = useMemo(
    () =>
      branchList.map((b) => ({
        value: b.id,
        label: renderBranchLabel(b),
      })),
    [branchList, renderBranchLabel]
  );

  const showBranchSwitchPreloader =
    isLoading && !SALES_SKELETON_ON_BRANCH_SWITCH.has(location.pathname);

  return (
    <>
      {/*
        必須 portal 到 body：右上角 cluster 有 transform（branch-select-slot-nudge），
        若 Preloader 掛在其子樹，fixed 會被困在該層，白底遮罩會蓋住分店選單與「我的權限」名稱。
      */}
      {showBranchSwitchPreloader &&
        typeof document !== "undefined" &&
        createPortal(<Preloader />, document.body)}

      <div
        className={`d-flex align-items-center me-4 new-header__store${wrapperClassName ? ` ${wrapperClassName}` : ""}`}
        style={{ position: "relative" }}
      >
        {showUserProfile ? <UserProfileMenu className='me-1' /> : null}
        <Select
          suffixIcon={<CaretDownOutlined />}
          className='changeSelect'
          value={branchList.length !== 1 ? currentBranchId : brandName}
          onChange={handleSwitchBranch}
          options={branchesOption}
          popupClassName='branch-select-dropdown-popup'
          dropdownRender={(menu) => (
            <div className='branch-select-dropdown'>
              <div className='branch-select-dropdown__menu'>{menu}</div>
              <div className='branch-select-dropdown__footer'>
                <button
                  type='button'
                  className='branch-select-dropdown__logout'
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={confirmLogout}
                >
                  {renderBranchLabel({ branch_name: "登出" }, { isLogout: true })}
                </button>
              </div>
            </div>
          )}
        />
      </div>
    </>
  );
};

export default BranchSelector;
